import { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  ButtonGroup,
  TextField,
  InputAdornment,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Collapse,
  Divider
} from '@mui/material';
import { getUsers, setUserAdminStatus, setUserDisabledStatus, deleteUser, type User } from '../api/client';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonIcon from '@mui/icons-material/Person';
import VerifiedIcon from '@mui/icons-material/Verified';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ClearIcon from '@mui/icons-material/Clear';

interface UserListProps {
  onRefresh?: () => void;
}

const UserList = forwardRef<{ refresh: () => void }, UserListProps>(({ onRefresh: _onRefresh }, ref) => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [updatingUsers, setUpdatingUsers] = useState<Set<string>>(new Set());
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; user: User | null }>({ 
    open: false, 
    user: null 
  });

  // Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    showAdmins: true,
    showRegularUsers: true,
    showActive: true,
    showDisabled: true,
    showRecentlyActive: true, // Last 30 days
    showInactive: true
  });

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const userList = await getUsers();
      setUsers(userList);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  // Filter and search logic
  useEffect(() => {
    let filtered = [...users];

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(user =>
        user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.display_name && user.display_name.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply role filters
    if (!filters.showAdmins || !filters.showRegularUsers) {
      filtered = filtered.filter(user => {
        if (user.is_admin && !filters.showAdmins) return false;
        if (!user.is_admin && !filters.showRegularUsers) return false;
        return true;
      });
    }

    // Apply status filters
    if (!filters.showActive || !filters.showDisabled) {
      filtered = filtered.filter(user => {
        if (!user.disabled && !filters.showActive) return false;
        if (user.disabled && !filters.showDisabled) return false;
        return true;
      });
    }

    // Apply recent activity filters
    if (!filters.showRecentlyActive || !filters.showInactive) {
      const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
      filtered = filtered.filter(user => {
        const isRecentlyActive = user.last_sign_in_time && user.last_sign_in_time > thirtyDaysAgo;
        if (isRecentlyActive && !filters.showRecentlyActive) return false;
        if (!isRecentlyActive && !filters.showInactive) return false;
        return true;
      });
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, filters]);

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleFilterChange = (filterKey: keyof typeof filters) => {
    setFilters(prev => ({
      ...prev,
      [filterKey]: !prev[filterKey]
    }));
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      showAdmins: true,
      showRegularUsers: true,
      showActive: true,
      showDisabled: true,
      showRecentlyActive: true,
      showInactive: true
    });
  };

  const getActiveFilterCount = () => {
    const defaultFilters = {
      showAdmins: true,
      showRegularUsers: true,
      showActive: true,
      showDisabled: true,
      showRecentlyActive: true,
      showInactive: true
    };
    
    let count = 0;
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== defaultFilters[key as keyof typeof defaultFilters]) {
        count++;
      }
    });
    
    if (searchTerm) count++;
    return count;
  };

  const handleAdminToggle = async (user: User) => {
    const userId = user.uid;
    setUpdatingUsers(prev => new Set(prev).add(userId));
    
    try {
      await setUserAdminStatus(userId, !user.is_admin);
      await fetchUsers(); // Refresh the user list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update admin status');
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleDisableToggle = async (user: User) => {
    const userId = user.uid;
    setUpdatingUsers(prev => new Set(prev).add(userId));
    
    try {
      await setUserDisabledStatus(userId, !user.disabled);
      await fetchUsers(); // Refresh the user list
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update user status');
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const handleDeleteUser = async (user: User) => {
    const userId = user.uid;
    setUpdatingUsers(prev => new Set(prev).add(userId));
    
    try {
      await deleteUser(userId);
      await fetchUsers(); // Refresh the user list
      setDeleteDialog({ open: false, user: null });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
    } finally {
      setUpdatingUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  const openDeleteDialog = (user: User) => {
    setDeleteDialog({ open: true, user });
  };

  const closeDeleteDialog = () => {
    setDeleteDialog({ open: false, user: null });
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'Never';
    return new Date(timestamp).toLocaleDateString() + ' ' + new Date(timestamp).toLocaleTimeString();
  };

  // const formatTimeSince = (timestamp: number | null) => {
  //   if (!timestamp) return 'Never';
  //   const now = Date.now();
  //   const diffMs = now - timestamp;
  //   const diffMins = Math.floor(diffMs / (1000 * 60));
  //   const diffHours = Math.floor(diffMins / 60);
  //   const diffDays = Math.floor(diffHours / 24);
  
  //   if (diffDays > 0) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  //   if (diffHours > 0) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  //   if (diffMins > 0) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  //   return 'Just now';
  // };

  useImperativeHandle(ref, () => ({
    refresh: fetchUsers
  }));

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading users...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
        <Button 
          onClick={fetchUsers} 
          size="small" 
          sx={{ ml: 2 }}
        >
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">
          User Management ({filteredUsers.length} of {users.length} users)
        </Typography>
        <Button 
          onClick={fetchUsers}
          variant="outlined"
          size="small"
        >
          Refresh
        </Button>
      </Box>

      {/* Search and Filter Controls */}
      <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
          <TextField
            size="small"
            placeholder="Search users by email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1 }}
          />
          
          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={() => setShowFilters(!showFilters)}
            sx={{ minWidth: 'auto' }}
          >
            Filters {getActiveFilterCount() > 0 && `(${getActiveFilterCount()})`}
          </Button>
          
          {getActiveFilterCount() > 0 && (
            <Button
              variant="text"
              startIcon={<ClearIcon />}
              onClick={clearFilters}
              size="small"
            >
              Clear
            </Button>
          )}
        </Box>

        <Collapse in={showFilters}>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
            <FormGroup>
              <Typography variant="subtitle2" gutterBottom>Role</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.showAdmins}
                    onChange={() => handleFilterChange('showAdmins')}
                    size="small"
                  />
                }
                label="Admins"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.showRegularUsers}
                    onChange={() => handleFilterChange('showRegularUsers')}
                    size="small"
                  />
                }
                label="Regular Users"
              />
            </FormGroup>

            <FormGroup>
              <Typography variant="subtitle2" gutterBottom>Status</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.showActive}
                    onChange={() => handleFilterChange('showActive')}
                    size="small"
                  />
                }
                label="Active"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.showDisabled}
                    onChange={() => handleFilterChange('showDisabled')}
                    size="small"
                  />
                }
                label="Disabled"
              />
            </FormGroup>

            <FormGroup>
              <Typography variant="subtitle2" gutterBottom>Activity</Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.showRecentlyActive}
                    onChange={() => handleFilterChange('showRecentlyActive')}
                    size="small"
                  />
                }
                label="Recently Active (30 days)"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={filters.showInactive}
                    onChange={() => handleFilterChange('showInactive')}
                    size="small"
                  />
                }
                label="Inactive"
              />
            </FormGroup>
          </Box>
        </Collapse>
      </Paper>

      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Email</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Admin</strong></TableCell>
              <TableCell><strong>Last Sign-in</strong></TableCell>
              <TableCell><strong>Created</strong></TableCell>
              <TableCell><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredUsers.map((user) => (
              <TableRow key={user.uid} hover>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    {user.email_verified ? (
                      <VerifiedIcon color="success" fontSize="small" />
                    ) : (
                      <Tooltip title="Email not verified">
                        <VerifiedIcon color="disabled" fontSize="small" />
                      </Tooltip>
                    )}
                    {user.email}
                  </Box>
                </TableCell>
                <TableCell>
                  {user.disabled ? (
                    <Chip 
                      icon={<BlockIcon />}
                      label="Disabled" 
                      color="error" 
                      size="small" 
                    />
                  ) : (
                    <Chip 
                      icon={<PersonIcon />}
                      label="Active" 
                      color="success" 
                      size="small" 
                    />
                  )}
                </TableCell>
                <TableCell>
                  {user.is_admin ? (
                    <Chip 
                      icon={<AdminPanelSettingsIcon />}
                      label="Admin" 
                      color="primary" 
                      size="small" 
                    />
                  ) : (
                    <Chip 
                      label="User" 
                      color="default" 
                      size="small" 
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(user.last_sign_in_time)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(user.creation_time)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <ButtonGroup orientation="vertical" size="small" disabled={updatingUsers.has(user.uid)}>
                    <Button
                      onClick={() => handleAdminToggle(user)}
                      disabled={updatingUsers.has(user.uid)}
                      startIcon={user.is_admin ? <PersonIcon /> : <AdminPanelSettingsIcon />}
                      color={user.is_admin ? "warning" : "primary"}
                      variant="outlined"
                      size="small"
                    >
                      {user.is_admin ? 'Remove Admin' : 'Make Admin'}
                    </Button>
                    
                    <Button
                      onClick={() => handleDisableToggle(user)}
                      disabled={updatingUsers.has(user.uid)}
                      startIcon={user.disabled ? <LockOpenIcon /> : <LockIcon />}
                      color={user.disabled ? "success" : "warning"}
                      variant="outlined"
                      size="small"
                    >
                      {user.disabled ? 'Enable' : 'Disable'}
                    </Button>
                    
                    <Button
                      onClick={() => openDeleteDialog(user)}
                      disabled={updatingUsers.has(user.uid)}
                      startIcon={<DeleteIcon />}
                      color="error"
                      variant="outlined"
                      size="small"
                    >
                      Delete
                    </Button>
                  </ButtonGroup>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredUsers.length === 0 && users.length > 0 && (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No users match the current filters
          </Typography>
          <Button onClick={clearFilters} sx={{ mt: 1 }}>
            Clear Filters
          </Button>
        </Box>
      )}

      {users.length === 0 && (
        <Box sx={{ textAlign: 'center', p: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No users found
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={closeDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm User Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete the user account for{' '}
            <strong>{deleteDialog.user?.email}</strong>?
            <br />
            <br />
            This action cannot be undone. The user will be permanently removed from Firebase Authentication.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button 
            onClick={() => deleteDialog.user && handleDeleteUser(deleteDialog.user)} 
            color="error" 
            variant="contained"
            disabled={deleteDialog.user ? updatingUsers.has(deleteDialog.user.uid) : false}
            startIcon={updatingUsers.has(deleteDialog.user?.uid || '') ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {updatingUsers.has(deleteDialog.user?.uid || '') ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

export default UserList; 